import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import Stars from '@/images/Stars.svg';
import { SUPPORTED_LANGUAGES } from '@d-id/types';
import styled from '@emotion/styled';
import { Theme, useTheme } from '@mui/material';
import { useMemo } from 'react';

const AUTO_LANGUAGE = 'auto';

const StyledAdornmentSelect = styled(AdornmentSelect<string | null>)`
    margin-bottom: 1.6rem;
    width: 100%;

    & .MuiInputBase-root {
        height: 5.6rem;
        padding-top: 3px;
        padding-bottom: 3px;

        & .auto_detect_stars_icon {
            background-color: ${({ theme }) => theme.colors.secondary};
        }
    }

    & .MuiInputBase-input {
        font-size: 1.6rem;
        text-transform: capitalize;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
    }
`;

const StartIcon = styled.img`
    height: 20px;
    margin-right: 4px;
`;

const StarsIcon = styled(MaskedIcon)`
    margin-right: 10px;
`;

function getStartAdornment(theme: Theme, language: string | null): JSX.Element {
    const locale = SUPPORTED_LANGUAGES[language || ''];

    if (!locale) {
        return <StarsIcon className="auto_detect_stars_icon" src={Stars} size="20px" color={theme.colors.secondary.main} />;
    }

    const code = locale?.split('-')?.[1]?.toLowerCase();

    return code ? <StartIcon loading="lazy" src={`https://flagcdn.com/192x144/${code}.png`} alt={code} /> : <></>;
}

interface IProps {
    value: string | null;
    onChange: (value: string | null) => void;
}

function SourceLanguageSelect({ value, onChange }: IProps) {
    const theme = useTheme();
    const options = useMemo(() => [AUTO_LANGUAGE, ...Object.keys(SUPPORTED_LANGUAGES).sort()], []);

    function handleChange(_: any, value: string | null) {
        if (!value) {
            return;
        }
        if (value === AUTO_LANGUAGE) {
            onChange(null);
        } else {
            onChange(value);
        }
    }

    function getOptionLabel(option: string | null) {
        if (!option || option === AUTO_LANGUAGE) {
            return 'Auto detect';
        }
        return option;
    }

    const themedGetStartAdornment = (language: string | null) => getStartAdornment(theme, language);

    return (
        <StyledAdornmentSelect
            id="translation_source_language"
            label=""
            options={options}
            getOptionLabel={getOptionLabel}
            value={value || AUTO_LANGUAGE}
            optionSx={{ fontSize: '1.6rem', textTransform: 'capitalize', height: '5rem' }}
            onChange={handleChange}
            getStartAdornment={themedGetStartAdornment}
            disableClearable
        />
    );
}

export default SourceLanguageSelect;
